import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import { Check, X, Clock, ExternalLink, ImageIcon, Pencil, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { EventStyleBadges } from "../calendar/DanceStyleBadge";
import { formatTimeRange } from "@/lib/dateUtils";
import { useModeratorRole } from "../../hooks/useModeratorRole";
import type { Doc, Id } from "convex/_generated/dataModel";
import { EventEditModal } from "../forms/EventEditModal";
import { RecurringActionDialog } from "../calendar/RecurringActionDialog";

/**
 * A queue row is the event document itself plus its verification, so it can be
 * handed straight to the shared edit modal.
 */
type EventDoc = Doc<"events"> & {
  verification: null | {
    method: string;
    flyerUrl: string | null;
    externalUrl?: string;
  };
};

export function ModerationQueue() {
  const pendingEvents = useQuery(api.moderation.getPendingQueue);
  const approveEvent = useMutation(api.moderation.approveEvent);
  const rejectEvent = useMutation(api.moderation.rejectEvent);
  const moderateSeries = useMutation(api.moderation.moderateSeriesEvents);
  const { isModerator } = useModeratorRole(); // moderator or admin

  // The event open in the shared editor, and whether the edit applies to the
  // whole series. A recurring event asks first, exactly as an event card does.
  const [editing, setEditing] = useState<EventDoc | null>(null);
  const [editScope, setEditScope] = useState<"single" | "all">("single");
  const [scopePrompt, setScopePrompt] = useState<EventDoc | null>(null);
  const [seriesActioning, setSeriesActioning] = useState<string | null>(null);

  if (pendingEvents === undefined) {
    return (
      <div className="space-y-3">
        <h2 className="font-semibold text-lg text-foreground flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Pending Events
        </h2>
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-24 bg-secondary/30 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <section>
      <h2 className="font-semibold text-lg text-foreground mb-4 flex items-center gap-2">
        <Clock className="w-5 h-5" />
        Pending Events ({pendingEvents.length})
      </h2>

      {pendingEvents.length === 0 ? (
        <div className="text-center py-8 bg-card rounded-xl border border-border/50">
          <p className="text-muted-foreground">No events pending review.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {(pendingEvents as EventDoc[]).map((event) => {
            const isSeriesActioning = seriesActioning === event.patternId;
            return (
              <div
                key={event._id}
                className="bg-card rounded-xl border border-border/50 p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-foreground">{event.title}</h3>
                      {event.patternId && (
                        <span className="text-xs text-muted-foreground bg-secondary px-1.5 py-0.5 rounded">
                          recurring
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {event.date} {formatTimeRange(event.startTime, event.endTime)} at{" "}
                      {event.venueName}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      <EventStyleBadges danceStyles={event.danceStyles} mainStyles={event.mainStyles} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Submitted by: {event.submittedBy}
                    </p>

                    {/* Verification evidence */}
                    {event.verification && (
                      <div className="mt-3 p-2 rounded-lg bg-secondary/30">
                        {event.verification.method === "flyer_photo" &&
                          event.verification.flyerUrl && (
                            <div className="flex items-center gap-2">
                              <ImageIcon className="w-4 h-4 text-muted-foreground" />
                              <a
                                href={event.verification.flyerUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline"
                              >
                                View flyer
                              </a>
                            </div>
                          )}
                        {event.verification.method === "external_link" &&
                          event.verification.externalUrl && (
                            <div className="flex items-center gap-2">
                              <ExternalLink className="w-4 h-4 text-muted-foreground" />
                              <a
                                href={event.verification.externalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline truncate"
                              >
                                {event.verification.externalUrl}
                              </a>
                            </div>
                          )}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 flex-shrink-0">
                    {/* Edit button — moderator+ only */}
                    {isModerator && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={() => {
                          if (event.patternId) {
                            setScopePrompt(event);
                          } else {
                            setEditScope("single");
                            setEditing(event);
                          }
                        }}
                        title="Edit event"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    {/* Approve entire series — shown for recurring events, moderator+ only */}
                    {isModerator && event.patternId && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-green-400 hover:text-green-300 hover:bg-green-500/10 gap-1 text-xs"
                        disabled={isSeriesActioning}
                        title="Approve all events in this recurring series"
                        onClick={async () => {
                          setSeriesActioning(event.patternId!);
                          try {
                            await moderateSeries({ patternId: event.patternId!, action: "approve" });
                          } finally {
                            setSeriesActioning(null);
                          }
                        }}
                      >
                        <RefreshCw className="w-3 h-3" />
                        <Check className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                      onClick={() => approveEvent({ eventId: event._id })}
                      title="Approve this event only"
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => rejectEvent({ eventId: event._id })}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {scopePrompt && scopePrompt.patternId && (
        <RecurringActionDialog
          action="edit"
          patternId={scopePrompt.patternId}
          onClose={() => setScopePrompt(null)}
          onThisEvent={() => {
            setEditScope("single");
            setEditing(scopePrompt);
            setScopePrompt(null);
          }}
          onAllEvents={() => {
            setEditScope("all");
            setEditing(scopePrompt);
            setScopePrompt(null);
          }}
        />
      )}

      {/* One editor for the whole product: whatever the submit form grows, the
          moderation queue gets without being touched. */}
      {editing && (
        <EventEditModal
          event={editing}
          editScope={editScope}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}
    </section>
  );
}
